import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { GameVersionProvider } from './contexts/GameVersionContext';
import { ScoreThresholdProvider } from './contexts/ScoreThresholdContext';
import { CommentProvider } from './contexts/CommentContext';
import { AchievementToastProvider } from './contexts/AchievementToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import VersionRedirect from './components/VersionRedirect';
import Footer from './components/Footer';
import UpdateAvailableToast from './components/UpdateAvailableToast';
import { isMobileViewport } from './utils/viewport';

// Page components — lazy-loaded so route bundles split out of main chunk.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SongLibrary = lazy(() => import('./pages/SongLibrary'));
const SongInformation = lazy(() => import('./pages/SongInformation'));
const Record = lazy(() => import('./pages/Record'));
const BingoList = lazy(() => import('./pages/BingoList'));
const Bingo = lazy(() => import('./pages/Bingo'));
const CreateBingo = lazy(() => import('./pages/CreateBingo'));
const Volforce = lazy(() => import('./pages/Volforce'));
const RivalComparisons = lazy(() => import('./pages/RivalComparisons'));
const DiffTable = lazy(() => import('./pages/DiffTable'));
const PlaylistList = lazy(() => import('./pages/PlaylistList'));
const MyPlaylist = lazy(() => import('./pages/MyPlaylist'));
const PlaylistDetail = lazy(() => import('./pages/PlaylistDetail'));
const PlaylistCreate = lazy(() => import('./pages/PlaylistCreate'));
const Login = lazy(() => import('./pages/Login'));
const Settings = lazy(() => import('./pages/Settings'));
const SynonymManagement = lazy(() => import('./pages/admin/SynonymManagement'));
const SongDifficultyManagement = lazy(() => import('./pages/admin/SongDifficultyManagement'));
const SongAddition = lazy(() => import('./pages/admin/SongAddition'));
const TagManagement = lazy(() => import('./pages/admin/TagManagement'));
const ChartTagEditor = lazy(() => import('./pages/admin/ChartTagEditor'));
const VolforceCalculator = lazy(() => import('./pages/util/VolforceCalculator'));
const UnlockInfo = lazy(() => import('./pages/util/UnlockInfo'));
const RecentHistoryExport = lazy(() => import('./pages/RecentHistoryExport'));
const TagRankingExport = lazy(() => import('./pages/TagRankingExport'));
const GroupComparison = lazy(() => import('./pages/util/GroupComparison'));
const VolforcePotential = lazy(() => import('./pages/util/VolforcePotential'));
const PreviousVolforce = lazy(() => import('./pages/util/PreviousVolforce'));
const SongRecommend = lazy(() => import('./pages/util/SongRecommend'));
const Analysis = lazy(() => import('./pages/util/Analysis'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const PreviewLayout = lazy(() => import('./preview/PreviewLayout'));
const PreviewMobileLayout = lazy(() => import('./preview/PreviewMobileLayout'));
const PreviewDashboard = lazy(() => import('./preview/pages/dashboard/PreviewDashboard'));
const PreviewMobileDashboard = lazy(() => import('./preview/pages/m/dashboard/PreviewMobileDashboard'));
const PreviewMobileSongList = lazy(() => import('./preview/pages/m/songs/PreviewMobileSongList'));
const PreviewMobileSongDetail = lazy(() => import('./preview/pages/m/song-detail/PreviewMobileSongDetail'));
const PreviewMobileRecord = lazy(() => import('./preview/pages/m/record/PreviewMobileRecord'));
const PreviewMobileDiffTable = lazy(() => import('./preview/pages/m/difftable/PreviewMobileDiffTable'));
const PreviewMobileRivals = lazy(() => import('./preview/pages/m/rivals/PreviewMobileRivals'));
const PreviewMobileDuel = lazy(() => import('./preview/pages/m/duel/PreviewMobileDuel'));
const PreviewMobileBingoList = lazy(() => import('./preview/pages/m/bingo/PreviewMobileBingoList'));
const PreviewMobileBingoDetail = lazy(() => import('./preview/pages/m/bingo/PreviewMobileBingoDetail'));
const PreviewMobileBingoCreate = lazy(() => import('./preview/pages/m/bingo/PreviewMobileBingoCreate'));
const PreviewMobilePlaylistList = lazy(() => import('./preview/pages/m/playlist/PreviewMobilePlaylistList'));
const PreviewMobileMyPlaylist = lazy(() => import('./preview/pages/m/playlist/PreviewMobileMyPlaylist'));
const PreviewMobilePlaylistDetail = lazy(() => import('./preview/pages/m/playlist/PreviewMobilePlaylistDetail'));
const PreviewMobilePlaylistCreate = lazy(() => import('./preview/pages/m/playlist/PreviewMobilePlaylistCreate'));
const PreviewMobileUtilLanding = lazy(() => import('./preview/pages/m/util/PreviewMobileUtilLanding'));
const PreviewMobileVolforceCalculator = lazy(() => import('./preview/pages/m/util/PreviewMobileVolforceCalculator'));
const PreviewMobileVolforceReverse = lazy(() => import('./preview/pages/m/util/PreviewMobileVolforceReverse'));
const PreviewMobileUnlockInfo = lazy(() => import('./preview/pages/m/util/PreviewMobileUnlockInfo'));
const PreviewMobileSongRecommend = lazy(() => import('./preview/pages/m/util/PreviewMobileSongRecommend'));
const PreviewMobilePreviousVolforce = lazy(() => import('./preview/pages/m/util/PreviewMobilePreviousVolforce'));
const PreviewMobileGroupComparison = lazy(() => import('./preview/pages/m/util/PreviewMobileGroupComparison'));
const PreviewMobileVolforcePotential = lazy(() => import('./preview/pages/m/util/PreviewMobileVolforcePotential'));
const PreviewMobileWeaponSongs = lazy(() => import('./preview/pages/m/util/PreviewMobileWeaponSongs'));
const PreviewMobileNoryosaiCalc = lazy(() => import('./preview/pages/m/util/PreviewMobileNoryosaiCalc'));
const PreviewMobileAnalysis = lazy(() => import('./preview/pages/m/util/PreviewMobileAnalysis'));
const PreviewMobileVolforce = lazy(() => import('./preview/pages/m/volforce/PreviewMobileVolforce'));
const PreviewSongList = lazy(() => import('./preview/pages/songs/PreviewSongList'));
const PreviewSongDetail = lazy(() => import('./preview/pages/song-detail/PreviewSongDetail'));
const PreviewRecord = lazy(() => import('./preview/pages/record/PreviewRecord'));
const PreviewKonasuteTimeline = lazy(() => import('./preview/pages/konasute-timeline/PreviewKonasuteTimeline'));
const PreviewDiffTable = lazy(() => import('./preview/pages/difftable/PreviewDiffTable'));
const PreviewTagAnalysis = lazy(() => import('./preview/pages/tag-analysis/PreviewTagAnalysis'));
const PreviewMobileTagAnalysis = lazy(() => import('./preview/pages/m/tag-analysis/PreviewMobileTagAnalysis'));
const PreviewEvent = lazy(() => import('./preview/pages/event/PreviewEvent'));
const PreviewMobileEvent = lazy(() => import('./preview/pages/m/event/PreviewMobileEvent'));
const PreviewVgcFinal = lazy(() => import('./preview/pages/event/PreviewVgcFinal'));
const PreviewMobileVgcFinal = lazy(() => import('./preview/pages/m/event/PreviewMobileVgcFinal'));
const PreviewDiffTableExport = lazy(() => import('./preview/pages/difftable/PreviewDiffTableExport'));
const PreviewRivals = lazy(() => import('./preview/pages/rivals/PreviewRivals'));
const PreviewDuel = lazy(() => import('./preview/pages/duel/PreviewDuel'));
const PreviewBingoList = lazy(() => import('./preview/pages/bingo/PreviewBingoList'));
const PreviewBingoDetail = lazy(() => import('./preview/pages/bingo/PreviewBingoDetail'));
const PreviewBingoCreate = lazy(() => import('./preview/pages/bingo/PreviewBingoCreate'));
const PreviewBingoExport = lazy(() => import('./preview/pages/bingo/PreviewBingoExport'));
const PreviewPlaylistList = lazy(() => import('./preview/pages/playlist/PreviewPlaylistList'));
const PreviewMyPlaylist = lazy(() => import('./preview/pages/playlist/PreviewMyPlaylist'));
const PreviewPlaylistDetail = lazy(() => import('./preview/pages/playlist/PreviewPlaylistDetail'));
const PreviewPlaylistCreate = lazy(() => import('./preview/pages/playlist/PreviewPlaylistCreate'));
const PreviewPlaylistExport = lazy(() => import('./preview/pages/playlist/PreviewPlaylistExport'));
const PreviewUtilLanding = lazy(() => import('./preview/pages/util/PreviewUtilLanding'));
const PreviewVolforceCalculator = lazy(() => import('./preview/pages/util/PreviewVolforceCalculator'));
const PreviewVolforceReverse = lazy(() => import('./preview/pages/util/PreviewVolforceReverse'));
const PreviewUnlockInfo = lazy(() => import('./preview/pages/util/PreviewUnlockInfo'));
const PreviewSongRecommend = lazy(() => import('./preview/pages/util/PreviewSongRecommend'));
const PreviewAdminLanding = lazy(() => import('./preview/pages/admin/PreviewAdminLanding'));
const PreviewSynonymManagement = lazy(() => import('./preview/pages/admin/PreviewSynonymManagement'));
const PreviewSongDifficultyManagement = lazy(() => import('./preview/pages/admin/PreviewSongDifficultyManagement'));
const PreviewSongAddition = lazy(() => import('./preview/pages/admin/PreviewSongAddition'));
const PreviewTagManagement = lazy(() => import('./preview/pages/admin/PreviewTagManagement'));
const PreviewProfileAssetManagement = lazy(() => import('./preview/pages/admin/PreviewProfileAssetManagement'));
const PreviewAchievementManagement = lazy(() => import('./preview/pages/admin/PreviewAchievementManagement'));
const PreviewEventOutboxManagement = lazy(() => import('./preview/pages/admin/PreviewEventOutboxManagement'));
const PreviewMatchManagement = lazy(() => import('./preview/pages/admin/PreviewMatchManagement'));
const PreviewRedeemCodeManagement = lazy(() => import('./preview/pages/admin/PreviewRedeemCodeManagement'));
const PreviewKonasuteHookManagement = lazy(() => import('./preview/pages/admin/PreviewKonasuteHookManagement'));
const PreviewChartTagEditor = lazy(() => import('./preview/pages/admin/chart-tag-editor/PreviewChartTagEditor'));
const PreviewMobileAdminLanding = lazy(() => import('./preview/pages/m/admin/PreviewMobileAdminLanding'));
const PreviewMobileSynonymManagement = lazy(() => import('./preview/pages/m/admin/PreviewMobileSynonymManagement'));
const PreviewMobileSongDifficultyManagement = lazy(() => import('./preview/pages/m/admin/PreviewMobileSongDifficultyManagement'));
const PreviewMobileSongAddition = lazy(() => import('./preview/pages/m/admin/PreviewMobileSongAddition'));
const PreviewMobileTagManagement = lazy(() => import('./preview/pages/m/admin/PreviewMobileTagManagement'));
const PreviewMobileChartTagEditor = lazy(() => import('./preview/pages/m/admin/PreviewMobileChartTagEditor'));
const PreviewMobileSettings = lazy(() => import('./preview/pages/m/settings/PreviewMobileSettings'));
const PreviewPreviousVolforce = lazy(() => import('./preview/pages/util/PreviewPreviousVolforce'));
const PreviewPreviousVolforceExport = lazy(() => import('./preview/pages/util/PreviewPreviousVolforceExport'));
const PreviewGroupComparison = lazy(() => import('./preview/pages/util/PreviewGroupComparison'));
const PreviewVolforcePotential = lazy(() => import('./preview/pages/util/PreviewVolforcePotential'));
const PreviewWeaponSongs = lazy(() => import('./preview/pages/util/PreviewWeaponSongs'));
const PreviewNoryosaiCalc = lazy(() => import('./preview/pages/util/PreviewNoryosaiCalc'));
const PreviewAnalysis = lazy(() => import('./preview/pages/util/PreviewAnalysis'));
const PreviewVolforce = lazy(() => import('./preview/pages/volforce/PreviewVolforce'));
const PreviewVolforceExport = lazy(() => import('./preview/pages/volforce/PreviewVolforceExport'));
const PreviewUserProfile = lazy(() => import('./preview/pages/user-profile/PreviewUserProfile'));
const PreviewMobileUserProfile = lazy(() => import('./preview/pages/m/user-profile/PreviewMobileUserProfile'));
const PreviewRecentHistoryExport = lazy(() => import('./preview/pages/recent-history/PreviewRecentHistoryExport'));
const PreviewSettings = lazy(() => import('./preview/pages/settings/PreviewSettings'));
const MonitorLayout = lazy(() => import('./preview/MonitorLayout'));
const PreviewMonitor = lazy(() => import('./preview/pages/monitor/PreviewMonitor'));
const MonitorRecords = lazy(() => import('./preview/pages/monitor/MonitorRecords'));
const MonitorRanking = lazy(() => import('./preview/pages/monitor/MonitorRanking'));
const MonitorWeapon = lazy(() => import('./preview/pages/monitor/MonitorWeapon'));
const MonitorVolforce = lazy(() => import('./preview/pages/monitor/MonitorVolforce'));
const PreviewUserOg = lazy(() => import('./preview/pages/og/PreviewUserOg'));
const PreviewSongOg = lazy(() => import('./preview/pages/og/PreviewSongOg'));
const PreviewCommonOg = lazy(() => import('./preview/pages/og/PreviewCommonOg'));

// Mobile redirect at the route boundary so PreviewLayout's lazy chunk
// (PreviewSidebar + PreviewTopbar + OnboardingModal subtree, ~80KB) never
// gets loaded on phone viewports just to be discarded one render later.
function MobileRouteGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  if (isMobileViewport()) {
    const mobilePath = location.pathname === '/7'
      ? '/7/m/dashboard'
      : location.pathname.replace(/^\/7\//, '/7/m/');
    return <Navigate to={mobilePath + location.search + location.hash} replace />;
  }
  return <>{children}</>;
}

function RouteFallback() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: '#0b0d12',
        color: 'rgba(255,255,255,0.5)',
        fontSize: 12,
        zIndex: 1,
      }}
    >
      Loading…
    </div>
  );
}

// Versioned Routes component - contains all routes with :version prefix
function VersionedRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Root redirect to default version */}
        <Route path="/" element={<VersionRedirect />} />

      {/* Version 6 Routes */}
      <Route path="/6/login" element={<Login />} />
      <Route path="/6/register" element={<Navigate to="/6/login" replace />} />
      <Route path="/6" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/6/songs" element={
        <ProtectedRoute>
          <SongLibrary />
        </ProtectedRoute>
      } />
      <Route path="/6/record" element={
        <ProtectedRoute>
          <Record />
        </ProtectedRoute>
      } />
      <Route path="/6/record/:userId" element={<Record />} />
      <Route path="/6/user/:userId" element={<UserProfile />} />
      <Route path="/6/volforce/:userId" element={<Volforce />} />
      <Route path="/6/volforce" element={
        <ProtectedRoute>
          <Volforce />
        </ProtectedRoute>
      } />
      <Route path="/6/volforce/export" element={<Volforce exportMode={true} />} />
      <Route path="/6/volforce/export/all" element={<Volforce exportMode={true} recordType="ALL" />} />
      <Route path="/6/volforce/export/arcade" element={<Volforce exportMode={true} recordType="ARCADE" />} />
      <Route path="/6/volforce/export/konasute" element={<Volforce exportMode={true} recordType="KONASUTE" />} />
      <Route path="/6/rivals" element={
        <ProtectedRoute>
          <RivalComparisons />
        </ProtectedRoute>
      } />
      <Route path="/6/difftable" element={
        <ProtectedRoute>
          <DiffTable />
        </ProtectedRoute>
      } />
      <Route path="/6/difftable/custom" element={
        <ProtectedRoute>
          <DiffTable />
        </ProtectedRoute>
      } />
      <Route path="/6/difftable/:tableId" element={
        <ProtectedRoute>
          <DiffTable />
        </ProtectedRoute>
      } />
      <Route path="/6/difftable/export/custom" element={<DiffTable exportMode={true} isCustomExport={true} />} />
      <Route path="/6/difftable/export/:tableId" element={<DiffTable exportMode={true} />} />
      <Route path="/6/bingo" element={
        <ProtectedRoute>
          <BingoList />
        </ProtectedRoute>
      } />
      <Route path="/6/bingo/:bingoId" element={
        <ProtectedRoute>
          <Bingo />
        </ProtectedRoute>
      } />
      <Route path="/6/bingo/create" element={
        <ProtectedRoute>
          <CreateBingo />
        </ProtectedRoute>
      } />
      <Route path="/6/bingo/edit/:bingoId" element={
        <ProtectedRoute>
          <CreateBingo />
        </ProtectedRoute>
      } />
      <Route path="/6/bingo/export/:bingoId" element={<Bingo exportMode={true} />} />
      <Route path="/6/record/history/export" element={<RecentHistoryExport />} />
      <Route path="/6/playlist" element={
        <ProtectedRoute>
          <PlaylistList />
        </ProtectedRoute>
      } />
      <Route path="/6/playlist/my" element={
        <ProtectedRoute>
          <MyPlaylist />
        </ProtectedRoute>
      } />
      <Route path="/6/playlist/create" element={
        <ProtectedRoute>
          <PlaylistCreate />
        </ProtectedRoute>
      } />
      <Route path="/6/playlist/export/:playlistId" element={<PlaylistDetail exportMode={true} />} />
      <Route path="/6/playlist/:playlistId" element={<PlaylistDetail />} />
      <Route path="/6/playlist/:playlistId/edit" element={
        <ProtectedRoute>
          <PlaylistCreate />
        </ProtectedRoute>
      } />
      <Route path="/6/song/:songId/:difficulty" element={
        <ProtectedRoute>
          <SongInformation />
        </ProtectedRoute>
      } />
      <Route path="/6/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/6/admin/synonyms" element={
        <ProtectedRoute>
          <SynonymManagement />
        </ProtectedRoute>
      } />
      <Route path="/6/admin/song-difficulty" element={
        <ProtectedRoute>
          <SongDifficultyManagement />
        </ProtectedRoute>
      } />
      <Route path="/6/util/volforce-calc" element={<VolforceCalculator />} />
      <Route path="/6/util/unlock-info" element={<UnlockInfo />} />
      <Route path="/6/util/group-compare" element={
        <ProtectedRoute>
          <GroupComparison />
        </ProtectedRoute>
      } />
      <Route path="/6/util/recommend" element={
        <ProtectedRoute>
          <SongRecommend />
        </ProtectedRoute>
      } />

      {/* Version 7 Routes */}
      <Route path="/7/login" element={<Login />} />
      <Route path="/7/register" element={<Navigate to="/7/login" replace />} />
      <Route path="/7" element={<Navigate to="/7/dashboard" replace />} />

      {/* Legacy v7 routes (formerly /7/*, kept for backward compatibility) */}
      <Route path="/7/legacy" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/songs" element={
        <ProtectedRoute>
          <SongLibrary />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/record" element={
        <ProtectedRoute>
          <Record />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/record/:userId" element={<Record />} />
      <Route path="/7/legacy/user/:userId" element={<UserProfile />} />
      <Route path="/7/legacy/volforce/:userId" element={<Volforce />} />
      <Route path="/7/legacy/volforce" element={
        <ProtectedRoute>
          <Volforce />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/volforce/export" element={<Volforce exportMode={true} />} />
      <Route path="/7/legacy/volforce/export/all" element={<Volforce exportMode={true} recordType="ALL" />} />
      <Route path="/7/legacy/volforce/export/arcade" element={<Volforce exportMode={true} recordType="ARCADE" />} />
      <Route path="/7/legacy/volforce/export/konasute" element={<Volforce exportMode={true} recordType="KONASUTE" />} />
      <Route path="/7/legacy/rivals" element={
        <ProtectedRoute>
          <RivalComparisons />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/difftable" element={
        <ProtectedRoute>
          <DiffTable />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/difftable/custom" element={
        <ProtectedRoute>
          <DiffTable />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/difftable/:tableId" element={
        <ProtectedRoute>
          <DiffTable />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/difftable/export/custom" element={<DiffTable exportMode={true} isCustomExport={true} />} />
      <Route path="/7/legacy/difftable/export/:tableId" element={<DiffTable exportMode={true} />} />
      <Route path="/7/legacy/bingo" element={
        <ProtectedRoute>
          <BingoList />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/bingo/:bingoId" element={
        <ProtectedRoute>
          <Bingo />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/bingo/create" element={
        <ProtectedRoute>
          <CreateBingo />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/bingo/edit/:bingoId" element={
        <ProtectedRoute>
          <CreateBingo />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/bingo/export/:bingoId" element={<Bingo exportMode={true} />} />
      <Route path="/7/legacy/record/history/export" element={<RecentHistoryExport />} />
      <Route path="/7/legacy/playlist" element={
        <ProtectedRoute>
          <PlaylistList />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/playlist/my" element={
        <ProtectedRoute>
          <MyPlaylist />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/playlist/create" element={
        <ProtectedRoute>
          <PlaylistCreate />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/playlist/export/:playlistId" element={<PlaylistDetail exportMode={true} />} />
      <Route path="/7/legacy/playlist/:playlistId" element={<PlaylistDetail />} />
      <Route path="/7/legacy/playlist/:playlistId/edit" element={
        <ProtectedRoute>
          <PlaylistCreate />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/song/:songId/:difficulty" element={
        <ProtectedRoute>
          <SongInformation />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/admin/synonyms" element={
        <ProtectedRoute>
          <SynonymManagement />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/admin/song-difficulty" element={
        <ProtectedRoute>
          <SongDifficultyManagement />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/admin/add-song" element={
        <ProtectedRoute>
          <SongAddition />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/admin/tags" element={
        <ProtectedRoute>
          <TagManagement />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/admin/tag-editor" element={
        <ProtectedRoute>
          <ChartTagEditor />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/util/volforce-calc" element={<VolforceCalculator />} />
      <Route path="/7/legacy/util/unlock-info" element={<UnlockInfo />} />
      <Route path="/7/legacy/util/group-compare" element={
        <ProtectedRoute>
          <GroupComparison />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/util/volforce-potential" element={
        <ProtectedRoute>
          <VolforcePotential />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/util/previous-volforce" element={
        <ProtectedRoute>
          <PreviousVolforce />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/util/previous-volforce/export" element={<PreviousVolforce exportMode={true} />} />
      <Route path="/7/legacy/tag/export/ranking" element={<TagRankingExport />} />
      <Route path="/7/legacy/util/recommend" element={
        <ProtectedRoute>
          <SongRecommend />
        </ProtectedRoute>
      } />
      <Route path="/7/legacy/util/analysis" element={
        <ProtectedRoute requireAdmin>
          <Analysis />
        </ProtectedRoute>
      } />

      {/* OG render routes (no auth, no layout, fixed 1200x630, called by backend Playwright) */}
      <Route path="/:version/og/render/user/:userId" element={<PreviewUserOg />} />
      <Route path="/:version/og/render/song/:songId/:difficulty" element={<PreviewSongOg />} />
      <Route path="/:version/og/render/difftable/:tableId" element={<PreviewCommonOg variant="difftable" />} />
      <Route path="/:version/og/render/bingo/:bingoId" element={<PreviewCommonOg variant="bingo" />} />
      <Route path="/:version/og/render/playlist/:playlistId" element={<PreviewCommonOg variant="playlist" />} />
      <Route path="/:version/og/render/default" element={<PreviewCommonOg variant="default" />} />

      {/* Promoted main UI export routes (no layout, must match BEFORE /7 parent route) */}
      <Route path="/7/difftable/export/custom" element={<PreviewDiffTableExport />} />
      <Route path="/7/difftable/export/:tableId" element={<PreviewDiffTableExport />} />
      <Route path="/7/volforce/export/:recordType" element={<PreviewVolforceExport />} />
      <Route path="/7/util/previous-volforce/export" element={<PreviewPreviousVolforceExport />} />
      <Route path="/7/bingo/export/:bingoId" element={<PreviewBingoExport />} />
      <Route path="/7/playlist/export/:playlistId" element={<PreviewPlaylistExport />} />
      <Route path="/7/record/history/export" element={<PreviewRecentHistoryExport />} />

      {/* Public profile (no login, must match BEFORE /7 parent route) */}
      <Route path="/7/user/:userId" element={
        <MobileRouteGuard>
          <PreviewUserProfile />
        </MobileRouteGuard>
      } />
      <Route path="/7/m/user/:userId" element={<PreviewMobileUserProfile />} />

      {/* Monitor UI — standalone shell (own layout), MUST match BEFORE /7 parent route */}
      <Route path="/7/monitor" element={
        <ProtectedRoute>
          <MonitorLayout />
        </ProtectedRoute>
      }>
        <Route index element={<PreviewMonitor />} />
        <Route path="records" element={<MonitorRecords />} />
        <Route path="ranking" element={<MonitorRanking />} />
        <Route path="weapon" element={<MonitorWeapon />} />
        <Route path="volforce" element={<MonitorVolforce />} />
      </Route>

      {/* Promoted main UI (v7 NABLA design) */}
      <Route path="/7" element={
        <ProtectedRoute>
          <MobileRouteGuard>
            <PreviewLayout />
          </MobileRouteGuard>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/7/dashboard" replace />} />
        <Route path="dashboard" element={<PreviewDashboard />} />
        <Route path="songs" element={<PreviewSongList />} />
        <Route path="song/:songId/:difficulty" element={<PreviewSongDetail />} />
        <Route path="record" element={<PreviewRecord />} />
        <Route path="record/:userId" element={<PreviewRecord />} />
        <Route path="record/:userId/difftable" element={<PreviewDiffTable />} />
        <Route path="record/:userId/difftable/custom" element={<PreviewDiffTable />} />
        <Route path="record/:userId/difftable/:tableId" element={<PreviewDiffTable />} />
        <Route path="konasute-timeline" element={<PreviewKonasuteTimeline />} />
        <Route path="difftable" element={<PreviewDiffTable />} />
        <Route path="difftable/custom" element={<PreviewDiffTable />} />
        <Route path="difftable/:tableId" element={<PreviewDiffTable />} />
        <Route path="tag-analysis" element={<PreviewTagAnalysis />} />
        <Route path="event" element={<PreviewEvent />} />
        <Route path="event/final" element={<PreviewVgcFinal />} />
        <Route path="rivals" element={<PreviewRivals />} />
        <Route path="duel/:opponentId" element={<PreviewDuel />} />
        <Route path="bingo" element={<PreviewBingoList />} />
        <Route path="bingo/create" element={<PreviewBingoCreate />} />
        <Route path="bingo/edit/:bingoId" element={<PreviewBingoCreate />} />
        <Route path="bingo/:bingoId" element={<PreviewBingoDetail />} />
        <Route path="playlist" element={<PreviewPlaylistList />} />
        <Route path="playlist/my" element={<PreviewMyPlaylist />} />
        <Route path="playlist/create" element={<PreviewPlaylistCreate />} />
        <Route path="playlist/:playlistId/edit" element={<PreviewPlaylistCreate />} />
        <Route path="playlist/:playlistId" element={<PreviewPlaylistDetail />} />
        <Route path="util" element={<PreviewUtilLanding />} />
        <Route path="util/volforce-calc" element={<PreviewVolforceCalculator />} />
        <Route path="util/volforce-reverse" element={<PreviewVolforceReverse />} />
        <Route path="util/unlock-info" element={<PreviewUnlockInfo />} />
        <Route path="util/recommend" element={<PreviewSongRecommend />} />
        <Route path="util/previous-volforce" element={<PreviewPreviousVolforce />} />
        <Route path="util/group-compare" element={<PreviewGroupComparison />} />
        <Route path="util/volforce-potential" element={<PreviewVolforcePotential />} />
        <Route path="util/weapon-songs" element={<PreviewWeaponSongs />} />
        <Route path="util/noryosai-calc" element={<PreviewNoryosaiCalc />} />
        <Route path="util/analysis" element={<PreviewAnalysis />} />
        <Route path="volforce" element={<PreviewVolforce />} />
        <Route path="volforce/:userId" element={<PreviewVolforce />} />
        <Route path="settings" element={<PreviewSettings />} />
        <Route path="admin" element={<PreviewAdminLanding />} />
        <Route path="admin/synonyms" element={<PreviewSynonymManagement />} />
        <Route path="admin/song-difficulty" element={<PreviewSongDifficultyManagement />} />
        <Route path="admin/add-song" element={<PreviewSongAddition />} />
        <Route path="admin/tags" element={<PreviewTagManagement />} />
        <Route path="admin/tag-editor" element={<PreviewChartTagEditor />} />
        <Route path="admin/profile-assets" element={<PreviewProfileAssetManagement />} />
        <Route path="admin/achievements" element={<PreviewAchievementManagement />} />
        <Route path="admin/event-outbox" element={<PreviewEventOutboxManagement />} />
        <Route path="admin/vgc-matches" element={<PreviewMatchManagement />} />
        <Route path="admin/redeem-codes" element={<PreviewRedeemCodeManagement />} />
        <Route path="admin/konasute-hook" element={<PreviewKonasuteHookManagement />} />

      </Route>

      {/* Promoted main UI - mobile (phone viewport) */}
      <Route path="/7/m" element={
        <ProtectedRoute>
          <PreviewMobileLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/7/m/dashboard" replace />} />
        <Route path="dashboard" element={<PreviewMobileDashboard />} />
        <Route path="songs" element={<PreviewMobileSongList />} />
        <Route path="song/:songId/:difficulty" element={<PreviewMobileSongDetail />} />
        <Route path="record" element={<PreviewMobileRecord />} />
        <Route path="record/:userId" element={<PreviewMobileRecord />} />
        <Route path="record/:userId/difftable" element={<PreviewMobileDiffTable />} />
        <Route path="record/:userId/difftable/custom" element={<PreviewMobileDiffTable />} />
        <Route path="record/:userId/difftable/:tableId" element={<PreviewMobileDiffTable />} />
        <Route path="konasute-timeline" element={<PreviewKonasuteTimeline />} />
        <Route path="difftable" element={<PreviewMobileDiffTable />} />
        <Route path="difftable/custom" element={<PreviewMobileDiffTable />} />
        <Route path="difftable/:tableId" element={<PreviewMobileDiffTable />} />
        <Route path="tag-analysis" element={<PreviewMobileTagAnalysis />} />
        <Route path="event" element={<PreviewMobileEvent />} />
        <Route path="event/final" element={<PreviewMobileVgcFinal />} />
        <Route path="rivals" element={<PreviewMobileRivals />} />
        <Route path="duel/:opponentId" element={<PreviewMobileDuel />} />
        <Route path="bingo" element={<PreviewMobileBingoList />} />
        <Route path="bingo/create" element={<PreviewMobileBingoCreate />} />
        <Route path="bingo/edit/:bingoId" element={<PreviewMobileBingoCreate />} />
        <Route path="bingo/:bingoId" element={<PreviewMobileBingoDetail />} />
        <Route path="playlist" element={<PreviewMobilePlaylistList />} />
        <Route path="playlist/my" element={<PreviewMobileMyPlaylist />} />
        <Route path="playlist/create" element={<PreviewMobilePlaylistCreate />} />
        <Route path="playlist/:playlistId/edit" element={<PreviewMobilePlaylistCreate />} />
        <Route path="playlist/:playlistId" element={<PreviewMobilePlaylistDetail />} />
        <Route path="util" element={<PreviewMobileUtilLanding />} />
        <Route path="util/volforce-calc" element={<PreviewMobileVolforceCalculator />} />
        <Route path="util/volforce-reverse" element={<PreviewMobileVolforceReverse />} />
        <Route path="util/unlock-info" element={<PreviewMobileUnlockInfo />} />
        <Route path="util/recommend" element={<PreviewMobileSongRecommend />} />
        <Route path="util/previous-volforce" element={<PreviewMobilePreviousVolforce />} />
        <Route path="util/group-compare" element={<PreviewMobileGroupComparison />} />
        <Route path="util/volforce-potential" element={<PreviewMobileVolforcePotential />} />
        <Route path="util/weapon-songs" element={<PreviewMobileWeaponSongs />} />
        <Route path="util/noryosai-calc" element={<PreviewMobileNoryosaiCalc />} />
        <Route path="util/analysis" element={<PreviewMobileAnalysis />} />
        <Route path="volforce" element={<PreviewMobileVolforce />} />
        <Route path="volforce/:userId" element={<PreviewMobileVolforce />} />
        <Route path="admin" element={<PreviewMobileAdminLanding />} />
        <Route path="admin/synonyms" element={<PreviewMobileSynonymManagement />} />
        <Route path="admin/song-difficulty" element={<PreviewMobileSongDifficultyManagement />} />
        <Route path="admin/add-song" element={<PreviewMobileSongAddition />} />
        <Route path="admin/tags" element={<PreviewMobileTagManagement />} />
        <Route path="admin/tag-editor" element={<PreviewMobileChartTagEditor />} />
        <Route path="settings" element={<PreviewMobileSettings />} />
      </Route>

      {/* Legacy routes - redirect to versioned routes */}
      <Route path="/login" element={<VersionRedirect path="/login" />} />
      <Route path="/register" element={<VersionRedirect path="/register" />} />
      <Route path="/songs" element={<VersionRedirect path="/songs" />} />
      <Route path="/record" element={<VersionRedirect path="/record" />} />
      <Route path="/record/:userId" element={<VersionRedirect path="/record/:userId" />} />
      <Route path="/volforce" element={<VersionRedirect path="/volforce" />} />
      <Route path="/volforce/:userId" element={<VersionRedirect path="/volforce/:userId" />} />
      <Route path="/rivals" element={<VersionRedirect path="/rivals" />} />
      <Route path="/difftable" element={<VersionRedirect path="/difftable" />} />
      <Route path="/difftable/:tableId" element={<VersionRedirect path="/difftable/:tableId" />} />
      <Route path="/bingo" element={<VersionRedirect path="/bingo" />} />
      <Route path="/bingo/:bingoId" element={<VersionRedirect path="/bingo/:bingoId" />} />
      <Route path="/bingo/create" element={<VersionRedirect path="/bingo/create" />} />
      <Route path="/playlist" element={<VersionRedirect path="/playlist" />} />
      <Route path="/playlist/my" element={<VersionRedirect path="/playlist/my" />} />
      <Route path="/playlist/create" element={<VersionRedirect path="/playlist/create" />} />
      <Route path="/playlist/:playlistId" element={<VersionRedirect path="/playlist/:playlistId" />} />
      <Route path="/playlist/:playlistId/edit" element={<VersionRedirect path="/playlist/:playlistId/edit" />} />
      <Route path="/song/:songId/:difficulty" element={<VersionRedirect path="/song/:songId/:difficulty" />} />
      <Route path="/settings" element={<VersionRedirect path="/settings" />} />
      <Route path="/util/volforce-calc" element={<VersionRedirect path="/util/volforce-calc" />} />
      <Route path="/util/unlock-info" element={<VersionRedirect path="/util/unlock-info" />} />
      <Route path="/util/group-compare" element={<VersionRedirect path="/util/group-compare" />} />
      <Route path="/util/recommend" element={<VersionRedirect path="/util/recommend" />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<VersionRedirect />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <ScoreThresholdProvider>
          <Router>
            <GameVersionProvider>
              <CommentProvider>
                <AchievementToastProvider>
                  <div className="min-h-screen flex flex-col">
                    <div className="flex-1">
                      <VersionedRoutes />
                    </div>
                    <Footer />
                  </div>
                  <UpdateAvailableToast />
                </AchievementToastProvider>
              </CommentProvider>
            </GameVersionProvider>
          </Router>
        </ScoreThresholdProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
